import type { Directive, DirectiveBinding } from 'vue';

/**
 * This directive adds a touch target to the element.
 * The touch target makes sure the element is not too small for touch input.
 *
 * This is achieved by placing a `::before` pseudo-element and `position: relative`
 * on the element.
 *
 * You can also separate the touch-target and the interactive element (if you need
 * to set `::before` on your element) by using `v-touch-target:host='myRef'`.
 * If you want to set a position other than `relative`, you can use
 * `v-touch-target:host.selfManaged='myRef'`.
 *
 * @example
 * **1)**
 * ```html
 * <button v-touch-target>Click me!</button>
 * ```
 *
 * **2)**
 * ```html
 * <button ref="button" class="button-with-pseudo-element">
 *     <span v-touch-target:host="button"></span>
 *     Click me!
 * </button>
 * ```
 *
 * **3)**
 * ```html
 * <button ref="button" v-touch-target.selfManaged style="position: sticky; top: 0">Click me!</button>
 * ```
 * Important: If you use `selfManaged`, you need to set `position` on your element yourself!
 *
 * **4)**
 * ```html
 * <button ref="button" style="position: sticky; top: 0">
 *     <span v-touch-target:host.selfManaged="button"></span>
 *     Click me!
 * </button>
 * ```
 *
 * @param el element the directive is attached to
 * @param binding supports `host` arg and `selfManaged` modifier
 */
export const vTouchTarget: Directive<HTMLElement, HTMLElement | null | undefined, 'selfManaged', 'host'> = (el: HTMLElement, binding) => {
	const hostEl = getTouchTargetHostEl(el, binding);

	if (hostEl) {
		hostEl.classList.add('touch-target__host');

		if (binding.modifiers.selfManaged) {
			hostEl.classList.add('touch-target__host-self-managed');
		}
	}
	el.classList.add('touch-target');
};

const getTouchTargetHostEl = (el: HTMLElement, binding?: DirectiveBinding<HTMLElement | null | undefined, 'selfManaged', 'host'>) => {
	if (binding?.arg === undefined) {
		return el;
	}
	if (binding.arg === 'host') {
		if (binding.value === undefined || binding.value === null || binding.value instanceof HTMLElement) {
			return binding.value;
		} else {
			// prettier-ignore
			throw new Error('[TouchTarget] Argument \'host\' must be an HTMLElement');
		}
	} else {
		throw new Error(`[TouchTarget] Argument '${binding.arg}' is not supported, pass 'host' argument`);
	}
};
